import {
  isDefined,
  messageFromError,
  toResponseError,
  type AppScriptId,
  type ScriptData,
  type ScriptGetInput,
  type ScriptGetSuccess,
  type ScriptRemoveInput,
  type ScriptRemoveSuccess,
} from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { useTantalimMutation } from '../mutation'
import { type GetHookResponse, type RemoveHookResponse } from '../hooks'
import { CONNECT_VERSION_KEY, connectScriptKey } from './keys'

export function useConnectScript(scriptId?: AppScriptId, enabled: boolean = true): GetHookResponse<ScriptData> {
  const input: ScriptGetInput | undefined = isDefined(scriptId) ? { scriptId } : undefined

  return useTantalimQuery<ScriptGetInput | undefined, ScriptGetSuccess, ScriptData>({
    url: '/connect/script/get',
    queryKey: connectScriptKey(scriptId),
    input,
    convertResponse: ({ script }) => script,
    options: {
      enabled: isDefined(scriptId) && enabled,
    },
  })
}

export function useRemoveConnectScript(): RemoveHookResponse<ScriptRemoveInput> {
  const mutation = useTantalimMutation<ScriptRemoveInput, ScriptRemoveSuccess>({
    url: '/connect/script/remove',
  })
  const queryClient = useQueryClient()

  async function remove(input: ScriptRemoveInput): Promise<void> {
    try {
      await mutation.mutateAsync(input)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: connectScriptKey(input.scriptId) }),
        queryClient.invalidateQueries({ queryKey: [CONNECT_VERSION_KEY] }),
      ])
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return {
    remove,
    isRemoving: mutation.isPending,
    error: messageFromError(mutation.error),
  }
}
